import { isEmpty } from 'lodash-es';

import type { Site } from '@hotjar/site';
import { isAllowed } from '@hotjar/feature-flags';
import type { Tier, GatesMap, TiersMap } from '@hotjar/allowances';

import * as tokensApi from '../domains/api';
import type * as ApiTypes from '../domains/api';
import type { Integration } from '../types';
import { inHouseIntegrations } from '../config';

const checkIfIntegrationEnabled = (
  name: ApiTypes.IntegrationTokenName,
  settings: ApiTypes.IntegrationToken['settings'] | ApiTypes.OauthToken,
): boolean => {
  switch (name) {
    case tokensApi.IntegrationTokenName.GoogleOptimize:
      return (
        settings !== null &&
        !isEmpty(settings) &&
        (settings as ApiTypes.GoogleOptimizeSettings).tag_recordings !== false
      );
    case tokensApi.IntegrationTokenName.GoogleAnalytics: {
      const ga = settings as ApiTypes.GoogleAnalyticsSettings;
      return Boolean(ga?.send_hotjar_id || ga?.tag_sessions);
    }
    case tokensApi.IntegrationTokenName.Optimizely:
      return (
        settings !== null && (settings as ApiTypes.OptimizelySettings)?.tag_recordings !== false
      );
    case tokensApi.IntegrationTokenName.Zapier:
      return true;
    case tokensApi.IntegrationTokenName.Hubspot: {
      const isProviderHubspot = (settings as tokensApi.OauthToken)?.provider === 'hubspot';
      const isProviderHubspotOauth2 =
        (settings as tokensApi.OauthToken)?.provider === 'hubspot-oauth2';
      return isProviderHubspot || isProviderHubspotOauth2;
    }
    case tokensApi.IntegrationTokenName.Mixpanel:
      return settings !== null && (settings as ApiTypes.MixpanelSettings)?.send_events !== false;

    case tokensApi.IntegrationTokenName.Unbounce:
      return settings !== null && (settings as ApiTypes.UnbounceSettings)?.tag_recordings !== false;
    default:
      return false;
  }
};

const getLowestTier = (tiers: Tier[]): Tier | undefined => {
  if (!tiers || tiers.length === 0) {
    return undefined;
  }

  const orderedTiers: Tier[] = ['basic', 'plus', 'business', 'scale'];
  const minIndex = Math.min(...tiers.map((tier) => orderedTiers.indexOf(tier)));
  return minIndex !== -1 ? (orderedTiers[minIndex] as Tier) : undefined;
};

export const getIntegrationConfigWithGateState = (
  integration: Integration,
  gates: GatesMap,
  tiers: TiersMap,
): Integration => {
  const gatedIntegration = { ...integration };
  let integrationTiers: Tier[] = [];

  // We're assuming the same tier for all gates in a single integration
  if (integration.gate) {
    gatedIntegration.isGateOpen = gates[integration.gate];
    integrationTiers = [tiers[integration.gate]];
  }

  if (integration.askGate) {
    gatedIntegration.isAskGateOpen = gates[integration.askGate];
    integrationTiers = [...integrationTiers, tiers[integration.askGate]];
  }

  if (integration.observeGate) {
    gatedIntegration.isObserveGateOpen = gates[integration.observeGate];
    integrationTiers = [...integrationTiers, tiers[integration.observeGate]];
  }

  gatedIntegration.tier = getLowestTier(integrationTiers);

  return gatedIntegration;
};

// eslint-disable-next-line func-params-args/func-params
export const getAllIntegrationsConfig = (
  currentSite: Site,
  status: ApiTypes.AllIntegrationsStatus,
  allowances: {
    gates: GatesMap;
    tiers: TiersMap;
  },
): Integration[] => {
  const integrations: Integration[] = inHouseIntegrations
    .filter(
      // Filter out integrations disabled by feature flags
      (int) => !int.featureFlag || isAllowed(int.featureFlag, currentSite),
    )
    .map((int) => getIntegrationConfigWithGateState(int, allowances.gates, allowances.tiers));

  // tokens
  status.tokens.forEach(({ name, settings }) => {
    const configIndex = integrations.findIndex(
      (integration: Integration) => integration.tokenName === name,
    );
    if (configIndex > -1) {
      const isEnabled = checkIfIntegrationEnabled(name, settings);
      integrations[configIndex].settings = settings;
      integrations[configIndex].isEnabled = integrations[configIndex].isAlwaysOn || isEnabled;
    }
  });

  // oauth
  status.oAuthTokens.forEach((token) => {
    if (token.provider.includes('hubspot')) {
      const configIndex = integrations.findIndex(
        (integration) => integration.tokenName === tokensApi.IntegrationTokenName.Hubspot,
      );

      if (configIndex > -1) {
        const isEnabled = checkIfIntegrationEnabled(tokensApi.IntegrationTokenName.Hubspot, token);
        integrations[configIndex].settings = token;
        integrations[configIndex].isEnabled = integrations[configIndex].isAlwaysOn || isEnabled;
      }
    }
  });

  // slack
  const slackIndex = integrations.findIndex((integration) => integration.tokenName === 'Slack');
  integrations[slackIndex].isEnabled = status.slack.enabled;

  // MS Teams (Otter)
  const teamsIndex = integrations.findIndex(
    (integration) => integration.otterName === 'teams_webhook',
  );
  if (teamsIndex > -1) {
    integrations[teamsIndex].isEnabled = status.teams.enabled || false;
  }

  return integrations;
};

export const integrationGatesOpen = (int: Integration | null): boolean | null => {
  if (!int) {
    return null;
  }

  const gatesState = [int.isGateOpen, int.isAskGateOpen, int.isObserveGateOpen];
  // True if no gates defined or one gate is open
  return gatesState.every((gate) => gate === undefined) || gatesState.some((gate) => gate === true);
};
